import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useForm, useFieldArray } from 'react-hook-form';
import { useMutation, useQueryClient, useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Trash2, 
  Save, 
  ArrowLeft,
  Calendar,
  Clock,
  FileText,
  List,
  AlignLeft
} from 'lucide-react';
import { timesheetAPI, projectAPI } from '../services/api';
import { format, addDays, startOfWeek, endOfWeek } from 'date-fns';
import toast from 'react-hot-toast';
import { useNotifications } from '../contexts/NotificationContext';
import { useAuth } from '../contexts/AuthContext';

const CreateTimesheet = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [descriptionFormats, setDescriptionFormats] = useState({}); // Track format type for each entry
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { addNotification } = useNotifications();
  const { user } = useAuth();

const { register, control, handleSubmit, watch, formState: { errors } } = useForm({
    defaultValues: {
      startDate: format(startOfWeek(new Date(), { weekStartsOn: 0 }), 'yyyy-MM-dd'),
      endDate: format(endOfWeek(new Date(), { weekStartsOn: 0 }), 'yyyy-MM-dd'),
      description: '',
      entries: [] // Start with empty entries array
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'entries'
  });

  const watchedStartDate = watch('startDate');
  const watchedEndDate = watch('endDate');
  const watchedEntries = watch('entries') || [];

  // Fetch assigned projects
  const { data: projects, refetch: refetchProjects } = useQuery(
    'my-assigned-projects',
    () => projectAPI.getMyAssignedProjects(),
    {
      select: (response) => response.data.projects,
    }
  );

  const { data: draftTimesheets } = useQuery(
    ['my-draft-timesheets'],
    () => timesheetAPI.getMyTimesheets({ status: 'DRAFT' }),
    {
      select: (response) => response.data?.timesheets || [],
    }
  );

  const redirectedTimesheetRef = useRef(null);

  useEffect(() => {
    if (!draftTimesheets || draftTimesheets.length === 0) return;
    if (!watchedStartDate || !watchedEndDate) return;

    const matchingDraft = draftTimesheets.find(
      (ts) => ts.startDate === watchedStartDate && ts.endDate === watchedEndDate
    );

    if (matchingDraft && redirectedTimesheetRef.current !== matchingDraft.id) {
      redirectedTimesheetRef.current = matchingDraft.id;
      toast.success('Found an existing draft for this week. Loading it now.');
      navigate(`/timesheets/${matchingDraft.id}/edit`, { replace: true });
    }
  }, [draftTimesheets, watchedStartDate, watchedEndDate, navigate]);


  const createMutation = useMutation(
    (data) => timesheetAPI.createTimesheet(data),
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries('timesheets');
        
        // Create notification for admins, super admins, and managers
        const notification = {
          id: Date.now(),
          title: 'New Timesheet Submitted',
          message: `${user?.firstName} ${user?.lastName} submitted a timesheet for review`,
          type: 'timesheet_submitted',
          isRead: false,
          createdAt: new Date().toISOString(),
          employeeName: `${user?.firstName} ${user?.lastName}`,
          timesheetId: response.data?.timesheet?.id || Date.now()
        };
        
        addNotification(notification);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to create timesheet');
        setIsSubmitting(false);
      },
    }
  );

  const onSubmit = async (data, status = 'DRAFT') => {
    // Validate that entries with hours or descriptions have a project selected
    const entriesWithData = Array.isArray(data.entries) ? data.entries.filter(entry => {
      const hours = parseFloat(entry.hoursWorked);
      const hasHours = !Number.isNaN(hours) && hours > 0;
      const hasDescription = entry.taskDescription && entry.taskDescription.trim().length > 0;
      return hasHours || hasDescription;
    }) : [];

    // Check if any entry with data is missing a project
    const missingProject = entriesWithData.some(entry => {
      return !entry.projectId && !entry.projectName;
    });

    if (missingProject) {
      toast.error('Please select a project for all entries with hours or descriptions.');
      return;
    }

    // Validate that there is at least one entry with meaningful data
    const hasValidEntries = Array.isArray(data.entries) && data.entries.some(entry => {
      const hours = parseFloat(entry.hoursWorked);
      const hasHours = !Number.isNaN(hours) && hours > 0;
      const hasProject = entry.projectId || entry.projectName;
      const hasDescription = entry.taskDescription && entry.taskDescription.trim().length > 0;
      return hasHours || hasProject || hasDescription;
    });

    if (!hasValidEntries) {
      toast.error('Add at least one time entry before saving your timesheet.');
      return;
    }

    // Validate that no day exceeds 8 hours
    if (Array.isArray(data.entries) && data.entries.length > 0) {
      const hoursPerDay = {};
      data.entries.forEach(entry => {
        if (entry.entryDate && entry.hoursWorked) {
          const hours = parseFloat(entry.hoursWorked);
          if (!Number.isNaN(hours) && hours > 0) {
            const date = entry.entryDate;
            hoursPerDay[date] = (hoursPerDay[date] || 0) + hours;
          }
        }
      });

      // Check if any day exceeds 8 hours
      const exceededDays = Object.entries(hoursPerDay).filter(([date, totalHours]) => totalHours > 8);
      if (exceededDays.length > 0) {
        const [date, totalHours] = exceededDays[0];
        toast.error(`Cannot exceed 8 hours per day. Date ${date} has ${totalHours.toFixed(2)} hours total.`);
        return;
      }
    }

    // Add status to data
    const submissionData = {
      ...data,
      status: status
    };
    
    // Instant feedback - don't wait for API
    setIsSubmitting(true);
    
    // Show success toast immediately based on status
    if (status === 'SUBMITTED') {
      toast.success('Timesheet submitting...');
    } else {
      toast.success('Timesheet saved as draft...');
    }
    
    // Navigate immediately for instant feel
    setTimeout(() => {
      navigate('/timesheets');
    }, 300); // Small delay for smooth transition
    
    // Submit in background (fire and forget)
    createMutation.mutate(submissionData);
  };
  
  const handleSaveDraft = (data) => {
    onSubmit(data, 'DRAFT');
  };
  
  const handleSubmitTimesheet = (data) => {
    onSubmit(data, 'SUBMITTED');
  };

  const addEntry = () => {
    const lastEntry = fields[fields.length - 1];
    const lastDate = lastEntry ? new Date(lastEntry.entryDate) : new Date(watchedStartDate);
    const nextDate = addDays(lastDate, 1);
    
    if (nextDate <= new Date(watchedEndDate)) {
      append({
        entryDate: format(nextDate, 'yyyy-MM-dd'),
        hoursWorked: 8,
        projectId: '',
        projectName: '',
        taskDescription: '',
      });
    }
  };

  const addEntryForDate = (date) => {
    append({
      entryDate: date,
      hoursWorked: 8,
      projectId: '',
      projectName: '',
      taskDescription: '',
    });
  };

  const generateWeekEntries = () => {
    const startDate = new Date(watchedStartDate);
    const endDate = new Date(watchedEndDate);
    const newEntries = [];
    
    for (let date = new Date(startDate); date <= endDate; date = addDays(date, 1)) {
      newEntries.push({
        entryDate: format(date, 'yyyy-MM-dd'),
        hoursWorked: 8,
        projectId: '',
        projectName: '',
        taskDescription: '',
      });
    }
    
    // Clear existing entries and add new ones
    fields.forEach((_, index) => remove(index));
    newEntries.forEach(entry => append(entry));
  };

  const getTotalHours = () => {
    return watchedEntries.reduce((total, field) => {
      if (field && field.hoursWorked) {
        const hours = parseFloat(field.hoursWorked) || 0;
        return total + hours;
      }
      return total;
    }, 0);
  };


  const getEntriesByDate = () => {
    const grouped = {};
    // Use watchedEntries to get current form values instead of fields
    watchedEntries.forEach((field, index) => {
      if (field && field.entryDate) {
        const date = field.entryDate;
        if (!grouped[date]) {
          grouped[date] = [];
        }
        grouped[date].push({ ...field, index });
      }
    });
    return grouped;
  };

  const getDateRange = () => {
    const startDate = new Date(watchedStartDate);
    const endDate = new Date(watchedEndDate);
    const dates = [];
    let currentDate = new Date(startDate);
    
    while (currentDate <= endDate) {
      dates.push(format(currentDate, 'yyyy-MM-dd'));
      currentDate = addDays(currentDate, 1);
    }
    return dates;
  };

  const toggleDescriptionFormat = (entryIndex, formatType) => {
    setDescriptionFormats(prev => ({
      ...prev,
      [entryIndex]: formatType
    }));
  };

  const getDescriptionFormat = (entryIndex) => {
    return descriptionFormats[entryIndex] || 'paragraph'; // Default to paragraph
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center space-x-4"
      >
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => navigate('/timesheets')}
          className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </motion.button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Create Timesheet</h1>
          <p className="mt-2 rounded-lg bg-blue-50 px-4 py-2 text-sm text-blue-700 border border-blue-200">
            Add your work hours and project details.<strong> Choose the correct start and end dates for the week you are logging.</strong>
          </p>
        </div>
      </motion.div>

      <form onSubmit={(e) => e.preventDefault()} className="space-y-6">
        {/* Basic Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-lg shadow-sm p-6"
        >
          <h2 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="form-label">
                <Calendar className="w-4 h-4 inline mr-2" />
                Start Date
              </label>
              <input
                {...register('startDate', { required: 'Start date is required' })}
                type="date"
                className="form-input"
              />
              {errors.startDate && (
                <p className="mt-1 text-sm text-red-600">{errors.startDate.message}</p>
              )}
            </div>
            
            <div>
              <label className="form-label">
                <Calendar className="w-4 h-4 inline mr-2" />
                End Date
              </label>
              <input
                {...register('endDate', { required: 'End date is required' })}
                type="date"
                className="form-input"
              />
              {errors.endDate && (
                <p className="mt-1 text-sm text-red-600">{errors.endDate.message}</p>
              )}
            </div>
          </div>
          
          <div className="mt-6">
            <label className="form-label">
              <FileText className="w-4 h-4 inline mr-2" />
              Description (Optional)
            </label>
            <textarea
              {...register('description')}
              rows={3}
              className="form-input"
              placeholder="Add any additional notes or comments..."
            />
          </div>
        </motion.div>

        {/* Time Entries */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-lg shadow-sm p-6"
        >
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-medium text-gray-900">Time Entries</h2>
          </div>

          <div className="space-y-6">
            {getDateRange().map((date, dateIndex) => {
              const entriesForDate = getEntriesByDate()[date] || [];
              const totalHoursForDate = entriesForDate.reduce((total, entry) => total + (parseFloat(entry.hoursWorked) || 0), 0);
              
              return (
                <motion.div
                  key={date}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: dateIndex * 0.1 }}
                  className="border border-gray-200 rounded-lg p-4"
                >
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center space-x-3">
                      <h3 className="text-lg font-medium text-gray-900">
                        {format(new Date(date), 'EEEE, MMMM d, yyyy')}
                      </h3>
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                          {totalHoursForDate.toFixed(1)} hours
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          (8 - totalHoursForDate) >= 0 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {Math.max(0, (8 - totalHoursForDate).toFixed(1))}h remaining
                        </span>
                      </div>
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      type="button"
                      onClick={() => addEntryForDate(date)}
                      className="px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-1 text-sm"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Add Task</span>
                    </motion.button>
                  </div>
                  
                  <div className="space-y-3">
                    {entriesForDate.map((entry, entryIndex) => {
                      // Calculate remaining hours excluding current entry
                      const currentEntryHours = parseFloat(entry?.hoursWorked) || 0;
                      const otherEntriesHours = totalHoursForDate - currentEntryHours;
                      const remainingHours = 8 - otherEntriesHours;
                      
                      return (
                      <motion.div
                        key={entry.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: entryIndex * 0.1 }}
                        className="p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-start gap-4 mb-4">
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                              <label className="form-label mb-0">
                                <Clock className="w-4 h-4 inline mr-1" />
                                Hours
                              </label>
                              <span className={`text-xs font-semibold px-2 py-0.5 rounded ${
                                remainingHours >= 0 
                                  ? 'bg-green-50 text-green-700' 
                                  : 'bg-red-50 text-red-700'
                              }`}>
                                {Math.max(0, remainingHours.toFixed(1))}h remaining
                              </span>
                            </div>
                            <input
                              {...register(`entries.${entry.index}.hoursWorked`, { 
                                required: 'Hours is required',
                                min: { value: 0, message: 'Hours must be positive' },
                                max: { value: 24, message: 'Hours cannot exceed 24' }
                              })}
                              type="number"
                              step="0.5"
                              min="0"
                              max="24"
                              className={`form-input ${(8 - totalHoursForDate) < 0 ? 'border-red-300' : ''}`}
                              placeholder="0.0"
                            />
                            {errors.entries?.[entry.index]?.hoursWorked && (
                              <p className="mt-1 text-sm text-red-600">{errors.entries[entry.index].hoursWorked.message}</p>
                            )}
                            {(8 - totalHoursForDate) < 0 && (
                              <p className="mt-1 text-xs text-red-600 font-medium">
                                ⚠️ This day exceeds 8 hours ({totalHoursForDate.toFixed(1)}h total)
                              </p>
                            )}
                            {remainingHours >= 0 && remainingHours < 1 && remainingHours > 0 && (
                              <p className="mt-1 text-xs text-orange-600 font-medium">
                                💡 Only {remainingHours.toFixed(1)}h remaining for this day
                              </p>
                            )}
                          </div>
                        )}
                          
                          <div className="flex-[2]">
                            <label className="form-label">Project *</label>
                            <select
                              {...register(`entries.${entry.index}.projectId`, { 
                                required: 'Project is required' 
                              })}
                              className={`form-input ${errors.entries?.[entry.index]?.projectId ? 'border-red-500' : ''}`}
                              onChange={(e) => {
                                const selectedProject = projects?.find(p => p.id === parseInt(e.target.value));
                                // Set both projectId and projectName
                                register(`entries.${entry.index}.projectId`).onChange(e);
                                if (selectedProject) {
                                  // Update the projectName field as well
                                  const projectNameField = document.querySelector(`input[name="entries.${entry.index}.projectName"]`);
                                  if (projectNameField) {
                                    projectNameField.value = selectedProject.name;
                                  }
                                }
                              }}
                            >
                              <option value="">Select a project</option>
                              {projects?.map((project) => (
                                <option key={project.id} value={project.id}>
                                  {project.name}
                                </option>
                              ))}
                            </select>
                            {errors.entries?.[entry.index]?.projectId && (
                              <p className="mt-1 text-sm text-red-600">{errors.entries[entry.index].projectId.message}</p>
                            )}
                            <input
                              {...register(`entries.${entry.index}.projectName`)}
                              type="hidden"
                            />
                          </div>

                          <div className="flex items-end">
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              type="button"
                              onClick={() => remove(entry.index)}
                              className="p-2 text-red-600 hover:text-red-800 transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </motion.button>
                          </div>
                        </div>
                        
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <label className="form-label">Task Description</label>
                            <div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1">
                              <button
                                type="button"
                                onClick={() => toggleDescriptionFormat(entry.index, 'paragraph')}
                                className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                                  getDescriptionFormat(entry.index) === 'paragraph'
                                    ? 'bg-white text-primary-600 shadow-sm'
                                    : 'text-gray-600 hover:text-gray-900'
                                }`}
                                title="Paragraph format"
                              >
                                <AlignLeft className="w-4 h-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => toggleDescriptionFormat(entry.index, 'bullets')}
                                className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                                  getDescriptionFormat(entry.index) === 'bullets'
                                    ? 'bg-white text-primary-600 shadow-sm'
                                    : 'text-gray-600 hover:text-gray-900'
                                }`}
                                title="Bullet points format"
                              >
                                <List className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          <textarea
                            {...register(`entries.${entry.index}.taskDescription`)}
                            rows={3}
                            className="form-input resize-none"
                            placeholder={
                              getDescriptionFormat(entry.index) === 'bullets'
                                ? "Enter each task on a new line:\n• Task 1\n• Task 2\n• Task 3"
                                : "What did you work on?"
                            }
                          />
                          {getDescriptionFormat(entry.index) === 'bullets' && (
                            <p className="text-xs text-gray-500 mt-1">
                              💡 Tip: Start each line with • or - for bullet points
                            </p>
                          )}
                        </div>
                      </motion.div>
                      );
                    })}
                    
                    {entriesForDate.length === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        <Clock className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                        <p>No tasks added for this date</p>
                        <button
                          type="button"
                          onClick={() => addEntryForDate(date)}
                          className="mt-2 text-primary-600 hover:text-primary-500 text-sm font-medium"
                        >
                          Add your first task
                        </button>
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Total Hours */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-700">Total Hours:</span>
              <span className="text-lg font-bold text-gray-900">{getTotalHours()}</span>
            </div>
          </div>
        </motion.div>

        {/* Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="flex justify-end space-x-4"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            type="button"
            onClick={() => navigate('/timesheets')}
            className="btn-secondary"
          >
            Cancel
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            type="button"
            onClick={handleSubmit(handleSaveDraft)}
            disabled={isSubmitting || createMutation.isLoading}
            className="px-6 py-2.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting || createMutation.isLoading ? (
              <div className="spinner"></div>
            ) : (
              <>
                <Save className="w-4 h-4" />
                <span>Save as Draft</span>
              </>
            )}
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            type="button"
            onClick={handleSubmit(handleSubmitTimesheet)}
            disabled={isSubmitting || createMutation.isLoading}
            className="btn-primary flex items-center space-x-2"
          >
            {isSubmitting || createMutation.isLoading ? (
              <div className="spinner"></div>
            ) : (
              <>
                <Save className="w-4 h-4" />
                <span>Submit</span>
              </>
            )}
          </motion.button>
        </motion.div>
      </form>

    </div>
  );
};

export default CreateTimesheet;

